import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import reportServices from "../services/reportServices";

// `{ ungrouped: true }` excludes reports that belong to a report group (used by
// the flat Reports.jsx list, since grouped reports are shown nested under their
// parent group instead). Omitting the arg entirely preserves the original
// behavior/query-key for every other existing caller (e.g. Notes.jsx).
export const useFetchReports = ({ ungrouped } = {}) => {
  return useQuery({
    queryKey: ungrouped ? ["fetchReports", { ungrouped: true }] : ["fetchReports"],
    queryFn: () => reportServices.FetchReports(ungrouped ? { ungrouped: true } : {}),
    staleTime: 1 * 60 * 1000,
  });
};

export const useFetchReportDetails = (params) => {
  return useQuery({
    queryKey: ["fetchReportDetails", params],
    queryFn: () => reportServices.FetchReportDetails(params),
    staleTime: 1 * 60 * 1000,
  });
};

// Fetches full report once, then polls the lightweight /status/:id endpoint (tiny payload)
// while BullMQ is still processing instead of re-fetching the full 671 KB report every 5 s.
// When status says complete, a single full refetch is triggered automatically.
// Returns the standard react-query result plus `processingStatus` for the progress banner.
export const useFetchReportWithPolling = (id) => {
  const queryClient = useQueryClient();

  const detailsQuery = useQuery({
    queryKey: ["reportDetails", id],
    queryFn: () => reportServices.FetchReportDetails({ id }),
    enabled: !!id,
    staleTime: 0,
    refetchInterval: false, // status endpoint drives progress; full refetch happens on completion
  });

  const posts = detailsQuery.data?.data?.posts ?? [];
  const isProcessing = posts.length > 0 && !posts.every((p) => p.apiHit);

  const statusQuery = useQuery({
    queryKey: ["reportProcessingStatus", id],
    queryFn: () => reportServices.FetchReportStatus(id),
    enabled: !!id && isProcessing,
    staleTime: 0,
    refetchInterval: isProcessing ? 3000 : false,
  });

  // Reset the sentinel whenever the report id changes (navigating between reports).
  const wasProcessing = useRef(false);
  useEffect(() => {
    wasProcessing.current = false;
  }, [id]);

  // When the status endpoint flips to complete, do one full refetch to get final stats.
  useEffect(() => {
    if (isProcessing) wasProcessing.current = true;
    if (wasProcessing.current && statusQuery.data?.data?.isComplete) {
      wasProcessing.current = false;
      queryClient.invalidateQueries({ queryKey: ["reportDetails", id] });
    }
  }, [statusQuery.data?.data?.isComplete, isProcessing, id, queryClient]);

  // Return null when not processing so callers never see stale status data from a
  // disabled query, which would leave the progress banner visible after completion.
  return { ...detailsQuery, processingStatus: isProcessing ? (statusQuery.data?.data ?? null) : null };
};


// Fetches time-series snapshots for the engagement trend charts.
export const useFetchReportSnapshots = (id) => {
  return useQuery({
    queryKey: ['reportSnapshots', id],
    queryFn:  () => reportServices.FetchReportSnapshots(id),
    enabled:  !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useFetchRefreshInfo = (id, { enabled = true } = {}) => {
  return useQuery({
    queryKey: ['reportRefreshInfo', id],
    queryFn:  () => reportServices.FetchRefreshInfo(id),
    enabled:  !!id && enabled,
    staleTime: 0,
    refetchInterval: 30 * 1000,
  });
};

// Polls /status/:id every 2s while a manual "Update Now" refresh is in progress.
// Enabled only when isRefreshing=true — stops automatically once isComplete.
export const useFetchReportStatus = (id, isRefreshing) => {
  return useQuery({
    queryKey: ['reportStatus', id],
    queryFn:  () => reportServices.FetchReportStatus(id),
    enabled:  !!id && !!isRefreshing,
    staleTime: 0,
    refetchInterval: isRefreshing ? 2000 : false,
  });
};

// const useAddReportPost = () => {
//   const queryClient = useQueryClient();
  
//   return useMutation(({
//     mutationFn: (posts) => reportServices.AddReport(posts),
//     onMutate: (newPost) =>  {
//       queryClient.getQueriesData(["fetchReportDetails"]),
//       queryClient.setQueriesData(["fetchReportDetails"], (posts) => [newPost, ...(posts || [])] ) ;
//     },

//     // onError:,

//   }))

// };




export default { useFetchReports, useFetchReportDetails };
